/**
 * BookAppointmentFlow.js
 *
 * Patient-facing booking modal.
 *   Step 0 → Who is this for? (Self / Someone Else)
 *   Step 1 → Select or register person (if someone else)
 *   Step 2 → Therapist & Type (auto-determined from consultation history)
 *   Step 3 → Select date + time slot
 *   Step 4 → Select mode (Offline / Online)
 *   Step 5 → Payment (skipped if free) — fee from Services, UPI from Org
 *            → confirmed: professional WhatsApp invoice
 *
 * Props:
 *   patientCard  {string}
 *   patientName  {string}
 *   patientPhone {string}
 *   branch       {string}
 *   allowBookForOthers {boolean} — show "book for someone else" option
 *   onClose      {Function}
 *   onBooked     {Function(appointment)}
 */
import React, { useState, useEffect, useRef } from 'react';
import api from './api';

var WORK_START = 7;
var WORK_END   = 19;
var SLOT_MIN   = 50;
var MAX_PER_SLOT = 4;                       // max 4 patients per slot per therapist

// Dr. Deepak specific schedule
var DEEPAK_HOURS    = [[11, 14], [16, 18]]; // 11AM-2PM, 4PM-6PM
var DEEPAK_SLOT_MIN = 60;                   // 1-hour slots
var DEEPAK_MAX      = 4;                    // max 4 per slot
var DEEPAK_OFF_DAYS = [0];                  // Sunday off

function isDeepakUser(username) {
  return (username || '').toLowerCase() === 'deepak';
}
function therapistDisplayName(t) {
  if (!t) return '';
  if (isDeepakUser(t.username || (typeof t === 'string' ? t : ''))) return 'Dr. Deepak';
  var name = t.username || (typeof t === 'string' ? t : '');
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

function pad(n) { return String(n).padStart(2, '0'); }
function fmtTime12(h, m) {
  if (m === undefined) m = 0;
  var period = h >= 12 ? 'PM' : 'AM';
  var h12 = h % 12 || 12;
  return h12 + ':' + pad(m) + ' ' + period;
}
function fmtDate(d) {
  return new Date(d + (typeof d === 'string' && d.length === 10 ? 'T00:00:00+05:30' : '')).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}
function buildSlots(dateStr, therapistUsername) {
  var date = new Date(dateStr + 'T00:00:00+05:30');  // IST — consistent across all browsers
  var deepak = isDeepakUser(therapistUsername);
  var slotMin = deepak ? DEEPAK_SLOT_MIN : SLOT_MIN;
  var hourBlocks = deepak ? DEEPAK_HOURS : [[WORK_START, WORK_END]];
  var slots = [];
  hourBlocks.forEach(function(block) {
    var h = block[0], m = 0;
    var endH = block[1];
    while (h < endH) {
      var start = new Date(date);
      start.setHours(h, m, 0, 0);
      var end = new Date(start.getTime() + slotMin * 60000);
      slots.push({ start: start, end: end, label: fmtTime12(h, m) + ' \u2013 ' + fmtTime12(end.getHours(), end.getMinutes()) });
      var totalMin = h * 60 + m + slotMin;
      h = Math.floor(totalMin / 60);
      m = totalMin % 60;
    }
  });
  return slots;
}
function isMobileDevice() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
function upiLink(vpa, name, amount, txnRef) {
  return 'upi://pay?pa=' + encodeURIComponent(vpa) + '&pn=' + encodeURIComponent(name) + '&am=' + amount + '&tn=' + encodeURIComponent('Appt ' + txnRef) + '&cu=INR';
}
function qrImageUrl(data) {
  return 'https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=' + encodeURIComponent(data) + '&margin=10';
}

/* ─── Step bar — 6 steps ─────────────────────────────── */
function StepBar({ step, skipStep1 }) {
  var steps = ['For Who', 'Person', 'Type', 'Date & Slot', 'Mode', 'Payment'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
      {steps.map(function(s, i) {
        // Skip Step 1 dot when booking for self
        if (i === 1 && skipStep1) return null;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 10,
                background: i < step ? '#059669' : i === step ? '#2563eb' : '#e5e7eb',
                color: i <= step ? '#fff' : '#9ca3af',
              }}>
                {i < step ? '\u2713' : i + 1}
              </div>
              <div style={{ fontSize: 8, color: i === step ? '#2563eb' : '#9ca3af', marginTop: 2, whiteSpace: 'nowrap' }}>{s}</div>
            </div>
            {!(i === 1 && skipStep1) && i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: i < step ? '#059669' : '#e5e7eb', margin: '0 2px', marginBottom: 16 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '11px 0',
      background: '#1e3a5f', color: '#fff',
      border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    }}>{'\u2039'} Back</button>
  );
}

function NextBtn({ label, onClick, disabled }) {
  return (
    <button disabled={!!disabled} onClick={onClick} style={{
      flex: 2, padding: '11px 0',
      background: disabled ? '#e2e8f0' : '#2563eb',
      color: disabled ? '#9ca3af' : '#fff',
      border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}>{label}</button>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN BOOKING FLOW
═══════════════════════════════════════════════════════ */
export default function BookAppointmentFlow({ patientCard, patientName, patientPhone, branch, allowBookForOthers, onClose, onBooked, showToast }) {
  var URL = process.env.REACT_APP_URL;

  var [step, setStep]               = useState(0);
  var [bookingType, setBookingType] = useState('consultation');
  var [therapists, setTherapists]   = useState([]);
  var [selectedTherapist, setST]    = useState(null);
  var [selectedDate, setDate]       = useState(function() { var d = new Date(); return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); });
  var [slots, setSlots]             = useState([]);
  var [bookedSlots, setBookedSlots] = useState([]);
  var [selectedSlot, setSlot]       = useState(null);
  var [mode, setMode]               = useState('offline');
  var [payMode, setPayMode]         = useState('');
  var [booking, setBooking]         = useState(false);
  var [bookedAppt, setBookedAppt]   = useState(null);
  var [qrRef, setQrRef]             = useState(function() { return patientCard + '-' + Date.now(); });
  var [qrTimer, setQrTimer]         = useState(60);
  var [allServices, setAllServices] = useState([]);
  var [selectedService, setSelectedService] = useState(null);
  var [orgData, setOrgData]         = useState(null);
  var timerRef = useRef(null);

  // ── NEW: "Book for who" state ──
  var [bookingFor, setBookingFor]     = useState(null); // 'self' | 'other'
  var [targetPatient, setTargetPatient] = useState(null); // the person being booked for
  var [phoneSearch, setPhoneSearch]   = useState('');
  var [phoneResults, setPhoneResults] = useState([]);
  var [searchingPhone, setSearchingPhone] = useState(false);
  var [showRegForm, setShowRegForm]   = useState(false);
  var [regName, setRegName]           = useState('');
  var [regAge, setRegAge]             = useState('');
  var [regGender, setRegGender]       = useState('');
  var [regRelation, setRegRelation]   = useState('');
  var [regPhone, setRegPhone]         = useState('');
  var [registering, setRegistering]   = useState(false);
  var [bookingContext, setBookingContext] = useState(null);
  var [isFreeBooking, setIsFreeBooking]  = useState(false);
  var [loadingContext, setLoadingContext] = useState(false);
  var [therapistOnLeave, setTherapistOnLeave] = useState(false);

  /* ── Load therapists, services, org on mount ──────────── */
  useEffect(function() {
    api.get(URL + '/users/staff/all')
      .then(function(r) { setTherapists((r.data || []).filter(function(u) {
        return u.roles && u.roles.some(function(ro) { return ['therapist', 'doctor', 'owner'].indexOf(ro) !== -1; });
      })); })
      .catch(function() {});

    api.get(URL + '/services')
      .then(function(r) { setAllServices(r.data || []); })
      .catch(function() {});

    api.get(URL + '/orgs')
      .then(function(r) {
        var orgs = r.data || [];
        var match = orgs.find(function(o) { return branch && o.name && o.name.toLowerCase().indexOf(branch.toLowerCase()) !== -1; })
          || orgs.find(function(o) { return o.status === 'active'; }) || orgs[0];
        if (match) setOrgData(match);
      })
      .catch(function() {});

    // Fetch branch-level address/contact if available
    if (branch) {
      api.get(URL + '/branches/branch/' + encodeURIComponent(branch))
        .then(function(r) {
          if (r.data && r.data.address) {
            setOrgData(function(prev) {
              if (prev && prev.address) return prev; // org already has address
              return Object.assign({}, prev || {}, { address: r.data.address, phone1: r.data.phone1, phone2: r.data.phone2 });
            });
          }
        })
        .catch(function() {});
    }
  }, [URL, branch]);

  /* ── Fetch booking context for a patient ── */
  function fetchBookingContext(card) {
    setLoadingContext(true);
    setBookingContext(null);
    api.get(URL + '/appointments/public/booking-context/' + card)
      .then(function(r) { setBookingContext(r.data); })
      .catch(function() { setBookingContext({ hasConsultation: false, pendingVisits: 0, creditBalance: 0, status: 'unknown' }); })
      .finally(function() { setLoadingContext(false); });
  }

  /* ── Determine therapist designation for service lookup ── */
  var isDeepakSelected = selectedTherapist && (
    isDeepakUser(selectedTherapist.username) ||
    (selectedTherapist.roles || []).some(function(r) { return ['owner', 'solution'].indexOf(r) !== -1; })
  );
  var therapistDesignation = isDeepakSelected
    ? 'Doctor Deepak'
    : (selectedTherapist && selectedTherapist.designation ? selectedTherapist.designation : 'Senior Therapist');

  /* ── Auto-select service when bookingType or therapist changes ── */
  useEffect(function() {
    if (!allServices.length || !selectedTherapist) return;
    // If free booking, no need to match service
    if (isFreeBooking) { setSelectedService(null); return; }
    var filtered;
    if (bookingType === 'consultation' || bookingType === 'review') {
      var keyword = bookingType === 'review' ? 'review' : 'consult';
      filtered = allServices.filter(function(s) { return (s.name || '').toLowerCase().indexOf(keyword) !== -1; });
      if (!filtered.length && bookingType === 'review') {
        filtered = allServices.filter(function(s) { return (s.name || '').toLowerCase().indexOf('consult') !== -1; });
      }
    } else {
      var desig = therapistDesignation.toLowerCase();
      filtered = allServices.filter(function(s) {
        return (s.description || '').toLowerCase().indexOf('session') !== -1 &&
               (s.description || '').toLowerCase().indexOf(desig) !== -1;
      });
      if (!filtered.length) {
        filtered = allServices.filter(function(s) {
          var nm = (s.name || '').toLowerCase();
          return nm.indexOf('session') !== -1 || nm.indexOf('therapy') !== -1 || nm.indexOf('yoga') !== -1;
        });
      }
    }
    var branchMatch = filtered.find(function(s) { return s.branch && s.branch.toLowerCase() === (branch || '').toLowerCase(); });
    setSelectedService(branchMatch || filtered[0] || null);
  }, [bookingType, allServices, branch, selectedTherapist, therapistDesignation, isFreeBooking]);

  /* ── Load booked slots when therapist + date changes ───── */
  var isDeepakTherapist = isDeepakUser(selectedTherapist && selectedTherapist.username);
  useEffect(function() {
    if (!selectedTherapist || !selectedDate) return;
    var builtSlots = buildSlots(selectedDate, selectedTherapist.username);
    var now = new Date();
    var today = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    if (selectedDate === today && builtSlots.every(function(s) { return s.start < now; })) {
      var next = new Date(now);
      next.setDate(next.getDate() + 1);
      if (isDeepakUser(selectedTherapist.username) && next.getDay() === 0) {
        next.setDate(next.getDate() + 1);
      }
      setDate(next.getFullYear() + '-' + pad(next.getMonth() + 1) + '-' + pad(next.getDate()));
      return;
    }
    setSlots(builtSlots);
    setTherapistOnLeave(false);
    api.get(URL + '/appointments/public/slots/' + selectedTherapist.username + '?date=' + selectedDate)
      .then(function(r) {
        var data = r.data || {};
        if (data.onLeave) { setTherapistOnLeave(true); setBookedSlots([]); }
        else { setTherapistOnLeave(false); setBookedSlots(data.slots || (Array.isArray(data) ? data : [])); }
      })
      .catch(function() { setBookedSlots([]); });
  }, [URL, selectedTherapist, selectedDate]);

  /* ── Live polling: refresh booked slots every 15s on Step 3 ── */
  useEffect(function() {
    if (step !== 3 || !selectedTherapist || !selectedDate) return;
    var interval = setInterval(function() {
      api.get(URL + '/appointments/public/slots/' + selectedTherapist.username + '?date=' + selectedDate)
        .then(function(r) {
          var data = r.data || {};
          if (data.onLeave) { setTherapistOnLeave(true); setBookedSlots([]); }
          else { setTherapistOnLeave(false); setBookedSlots(data.slots || (Array.isArray(data) ? data : [])); }
        })
        .catch(function() {});
    }, 15000);
    return function() { clearInterval(interval); };
  }, [step, selectedTherapist, selectedDate, URL]);

  /* ── QR refresh timer ───────────────────────────────────── */
  useEffect(function() {
    if (step !== 5 || payMode !== 'online' || bookedAppt || isFreeBooking) return;
    setQrTimer(60);
    timerRef.current = setInterval(function() {
      setQrTimer(function(t) {
        if (t <= 1) { setQrRef(patientCard + '-' + Date.now()); return 60; }
        return t - 1;
      });
    }, 1000);
    return function() { clearInterval(timerRef.current); };
  }, [step, payMode, bookedAppt, patientCard, isFreeBooking]);

  /* ── Helpers ──────────────────────────────────────────────── */
  function getSlotCount(slot) {
    return bookedSlots.filter(function(b) {
      var bs = new Date(b.start), be = new Date(b.end);
      return slot.start < be && slot.end > bs;
    }).length;
  }
  function isSlotBusy(slot) {
    return getSlotCount(slot) >= MAX_PER_SLOT;
  }
  function isPastSlot(slot) { return slot.start < new Date(); }
  var isSunday = selectedDate ? new Date(selectedDate + 'T00:00:00+05:30').getDay() === 0 : false;
  var isDeepakSunday = isDeepakTherapist && isSunday;

  /* ── Fee computation ──────────────────────────────────── */
  var baseAmt  = selectedService ? selectedService.amount || 0 : 0;
  var sgstAmt  = Math.round(baseAmt * ((selectedService && selectedService.sgst) || 0) / 100);
  var cgstAmt  = Math.round(baseAmt * ((selectedService && selectedService.cgst) || 0) / 100);
  var totalAmt = isFreeBooking ? 0 : baseAmt + sgstAmt + cgstAmt;
  var upiVpa   = orgData ? orgData.upid || '' : '';

  /* ── Matching services for dropdown ─────────────────────── */
  var matchingServices = allServices.filter(function(s) {
    var nm = (s.name || '').toLowerCase();
    if (bookingType === 'consultation') return nm.indexOf('consult') !== -1;
    if (bookingType === 'review') return nm.indexOf('review') !== -1 || nm.indexOf('consult') !== -1;
    return nm.indexOf('session') !== -1 || nm.indexOf('therapy') !== -1 || nm.indexOf('yoga') !== -1;
  });

  /* ── Target patient info (the person being booked for) ─── */
  var tName  = targetPatient ? targetPatient.name : patientName;
  var tCard  = targetPatient ? targetPatient.patientCard : patientCard;
  var tPhone = targetPatient ? String(targetPatient.number || '') : patientPhone;

  /* ── Phone search ────────────────────────────────────────── */
  function handlePhoneSearch() {
    var phone = (phoneSearch || '').replace(/\D/g, '').trim();
    if (!phone || phone.length < 5) return;
    setSearchingPhone(true);
    setPhoneResults([]);
    api.get(URL + '/patients/profiles/by-phone/' + phone)
      .then(function(r) { setPhoneResults(r.data || []); })
      .catch(function() { setPhoneResults([]); })
      .finally(function() { setSearchingPhone(false); });
  }

  /* ── Quick register ──────────────────────────────────────── */
  function handleQuickRegister() {
    if (!regName.trim() || !regPhone.trim()) return;
    setRegistering(true);
    api.post(URL + '/appointments/public/quick-register', {
      name: regName.trim(),
      phone: regPhone.replace(/\D/g, ''),
      age: regAge || undefined,
      gender: regGender || undefined,
      relation: regRelation || undefined,
      branch: branch || '',
      registeredBy: patientCard,
    })
      .then(function(r) {
        var p = r.data;
        setTargetPatient(p);
        fetchBookingContext(p.patientCard);
        setStep(2);
      })
      .catch(function(e) {
        var errMsg = 'Registration error: ' + (e.response ? (e.response.data.error || e.response.data) : e.message);
        if (showToast) showToast(errMsg, 'error'); else alert(errMsg);
      })
      .finally(function() { setRegistering(false); });
  }

  /* ── Book ──────────────────────────────────────────────── */
  function handleBook() {
    if (booking) return;
    setBooking(true);
    var bookData = {
      patientCard: tCard,
      name: tName,
      number: tPhone,
      therapist: selectedTherapist.username,
      start: selectedSlot.start,
      end: selectedSlot.end,
      isOnline: mode === 'online',
      paymentMode: isFreeBooking ? '' : payMode,
      branch: branch || '',
      bookingType: bookingType,
      serviceId: selectedService ? selectedService._id : undefined,
      amount: totalAmt,
      isFree: isFreeBooking,
    };
    // If booked by someone else for this person
    if (bookingFor === 'other' && tCard !== patientCard) {
      bookData.bookedForCard = patientCard;
      bookData.relation = regRelation || '';
    }
    api.post(URL + '/appointments/public/book', bookData)
      .then(function(res) {
        var appt = res.data.appointment;
        setBookedAppt(appt);
        if (onBooked) onBooked(appt);
        shareInvoiceForAppt(appt);
      })
      .catch(function(e) {
        var errMsg = 'Booking error: ' + (e.response ? (e.response.data.error || e.response.data) : e.message);
        if (showToast) showToast(errMsg, 'error'); else alert(errMsg);
      })
      .finally(function() { setBooking(false); });
  }

  /* ── WhatsApp invoice — 3 message formats ─────────────── */
  function getAddressBlock() {
    var addr = orgData && orgData.address ? orgData.address : "Dr Deepak Sachdeva's\nMedical Yoga centre.\nHouse no 41,\nWest Avenue Road (central mkt road),\nPunjabi Bagh West, New Delhi-110026";
    return [
      '',
      'Address:',
      addr,
      '',
      '\uD83D\uDCCD Google map Location:',
      'https://maps.app.goo.gl/K4EbctubZdPPYH6cA',
    ];
  }

  function getContactBlock() {
    var p1 = orgData && orgData.phone1 ? String(orgData.phone1) : '01145525456';
    var p2 = orgData && orgData.phone2 ? '+91 ' + String(orgData.phone2) : '+91 9891 24 34 44';
    return [
      '',
      'For any assistance pls call',
      ' ' + p1,
      p2,
    ];
  }

  function generateInvoiceText(appt) {
    var a = appt || bookedAppt;
    var invNo = a ? 'TKT-' + (a._id || '').slice(-6).toUpperCase() : 'PENDING';
    var dateStr = fmtDate(selectedDate);
    var timeStr = selectedSlot ? selectedSlot.label : '\u2014';
    var modeStr = mode === 'online' ? '\uD83C\uDF10 Online Video Session' : '\uD83C\uDFE5 In-Person Visit';
    var thName = selectedTherapist ? therapistDisplayName(selectedTherapist) : '\u2014';
    var thPhone = selectedTherapist && selectedTherapist.number ? String(selectedTherapist.number) : '';
    var thLine = thName + (thPhone ? ' (' + thPhone + ')' : '');
    var isAwaitingConfirmation = a && a.paymentStatus === 'awaiting_confirmation';
    var isConfirmed = isFreeBooking || (!isAwaitingConfirmation);

    // Booked for/by line
    var bookedForLines = [];
    bookedForLines.push('Name: ' + (tName || '\u2014'));
    bookedForLines.push('Phone: ' + (tPhone || '\u2014'));
    if (tCard) bookedForLines.push('Card No: ' + tCard);
    else bookedForLines.push('Card No: Will be Auto generated at the reception.');
    if (bookingFor === 'other') {
      bookedForLines.push('Booked by: ' + patientName + (regRelation ? ' (' + regRelation + ')' : ''));
    }

    var lines = [];

    if (isConfirmed) {
      // ─── FORMAT 3: APPOINTMENT BOOKING CONFIRMED ───
      lines = [
        'APPOINTMENT BOOKING CONFIRMED',
        '___________',
        'Ref: ' + invNo,
        '',
        'Appointment Details',
        'Status: \u2705 Confirmed',
        '',
        'Date: ' + dateStr,
        'Time: ' + timeStr,
        'Mode: ' + modeStr,
        '',
      ];
      lines = lines.concat(bookedForLines);
      lines = lines.concat(getAddressBlock());
      lines.push('');
      if (mode !== 'online') {
        lines.push('\u2728 Please remember to bring your medical reports, if any.');
      } else {
        lines.push('\u2728 For online appointments please send your reports in advance in this number.');
      }
      lines.push('');
      lines.push('\uD83C\uDF3C Lift facility is available.');
      lines = lines.concat(getContactBlock());
    } else if (bookingType === 'review' || isDeepakUser(thName)) {
      // ─── FORMAT 1: REVIEW BOOKING WITH DR DEEPAK SACHDEVA ───
      lines = [
        'REVIEW BOOKING WITH DR DEEPAK SACHDEVA',
        '___________',
        'Ref: ' + invNo,
        '',
        'Date: ' + dateStr,
        'Time: ' + timeStr,
        'Mode: ' + modeStr,
        '',
        'Status: \uD83D\uDD33 Confirmation awaited by the Doctor',
        '',
        'Therapist: ' + thLine,
        '',
        'Booked for:',
      ];
      lines = lines.concat(bookedForLines);
      lines = lines.concat(getAddressBlock());
      lines = lines.concat(getContactBlock());
    } else {
      // ─── FORMAT 2: THERAPY APPOINTMENT BOOKING ───
      lines = [
        'THERAPY APPOINTMENT BOOKING',
        '___________',
        'Ref: ' + invNo,
        '',
        'Session Details',
        '',
        'Date: ' + dateStr,
        'Time: ' + timeStr,
        'Mode: ' + modeStr,
        '',
        'Status: \uD83D\uDD33 Confirmation awaited by the therapist',
        '',
        'Therapist: ' + thLine,
        '',
        'Booked for:',
      ];
      lines = lines.concat(bookedForLines);
      lines = lines.concat(getAddressBlock());
      lines = lines.concat(getContactBlock());
    }

    return lines.join('\n');
  }

  function shareInvoiceForAppt(appt) {
    try {
      var text = generateInvoiceText(appt);
      var phone = (tPhone || '').replace(/\D/g, '');
      if (!phone) return;
      var to = phone.length === 10 ? '91' + phone : phone;
      window.open('https://wa.me/' + to + '?text=' + encodeURIComponent(text), '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.warn('WhatsApp share failed:', err);
    }
  }

  function shareInvoice() { shareInvoiceForAppt(bookedAppt); }

  /* ── Card style helper ───────────────────────────────────── */
  var card = function(active, disabled) {
    return {
      border: '2px solid ' + (active ? '#2563eb' : disabled ? '#e5e7eb' : '#d1d5db'),
      borderRadius: 12, padding: '12px 16px', cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? '#eff6ff' : disabled ? '#f9fafb' : '#fff',
      opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
    };
  };

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 540, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '18px 22px', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 900, fontSize: 16 }}>{'\uD83D\uDCC5'} Book Appointment</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
              {targetPatient ? targetPatient.name + ' \u00B7 ' + targetPatient.patientCard : patientName + ' \u00B7 ' + patientCard}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>{'\u2715'} Close</button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          <StepBar step={step} skipStep1={bookingFor === 'self'} />

          {/* ─── STEP 0: Who is this for? ─────────────── */}
          {step === 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 14 }}>
                Who is this appointment for?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* For Myself */}
                <div
                  onClick={function() {
                    setBookingFor('self');
                    setTargetPatient({ patientCard: patientCard, name: patientName, number: patientPhone });
                    fetchBookingContext(patientCard);
                    setStep(2);
                  }}
                  style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 16 })}
                >
                  <span style={{ fontSize: 30 }}>{'\uD83D\uDE4B'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>For Myself</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Book appointment for {patientName}</div>
                  </div>
                  <div style={{ color: '#2563eb', fontSize: 18, fontWeight: 700 }}>{'\u203A'}</div>
                </div>

                {/* For Someone Else */}
                {allowBookForOthers !== false && (
                  <div
                    onClick={function() {
                      setBookingFor('other');
                      setPhoneSearch(String(patientPhone || ''));
                      setStep(1);
                    }}
                    style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 16 })}
                  >
                    <span style={{ fontSize: 30 }}>{'\uD83D\uDC65'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>For Someone Else</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Family member, friend, or new person</div>
                    </div>
                    <div style={{ color: '#2563eb', fontSize: 18, fontWeight: 700 }}>{'\u203A'}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── STEP 1: Select or Register Person ─────── */}
          {step === 1 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 14 }}>
                Select or Register Person
              </div>

              {/* Phone search */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Search by Phone Number</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="tel"
                    value={phoneSearch}
                    onChange={function(e) { setPhoneSearch(e.target.value); }}
                    placeholder="Enter phone number"
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                  />
                  <button
                    onClick={handlePhoneSearch}
                    disabled={searchingPhone}
                    style={{ padding: '9px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                  >
                    {searchingPhone ? '\u2026' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Results */}
              {phoneResults.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Found {phoneResults.length} person(s) on this number
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {phoneResults.map(function(p) {
                      return (
                        <div
                          key={p._id || p.patientCard}
                          onClick={function() {
                            setTargetPatient(p);
                            fetchBookingContext(p.patientCard);
                            setStep(2);
                          }}
                          style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 12 })}
                        >
                          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                            {(p.name || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{p.name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              {p.patientCard}{p.age ? ' \u00B7 ' + p.age + ' yrs' : ''}{p.gender ? ' \u00B7 ' + p.gender : ''}
                            </div>
                          </div>
                          <div style={{ color: '#2563eb', fontSize: 18, fontWeight: 700 }}>{'\u203A'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* If search done and no results, show new person inline form */}
              {searchingPhone === false && phoneResults.length === 0 && phoneSearch.replace(/\D/g, '').length >= 5 && (
                <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 14, padding: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f', marginBottom: 4 }}>No existing person found</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>Enter details for a new person:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Name *</label>
                      <input value={regName} onChange={function(e) { setRegName(e.target.value); }}
                        placeholder="Full name"
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Phone Number *</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={function() { setRegPhone(phoneSearch.replace(/\D/g, '')); }}
                          style={{ padding: '8px 12px', border: '1px solid ' + (regPhone === phoneSearch.replace(/\D/g, '') ? '#2563eb' : '#d1d5db'), borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: regPhone === phoneSearch.replace(/\D/g, '') ? '#eff6ff' : '#fff', color: '#1e3a5f', whiteSpace: 'nowrap' }}>
                          Same Number
                        </button>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>or</span>
                        <input type="tel" value={regPhone} onChange={function(e) { setRegPhone(e.target.value); }}
                          placeholder="Different number"
                          style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>This number will be used for all future communication</div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Age (optional)</label>
                      <input type="number" value={regAge} onChange={function(e) { setRegAge(e.target.value); }}
                        placeholder="Age"
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                    <button
                      onClick={handleQuickRegister}
                      disabled={registering || !regName.trim() || !regPhone.trim()}
                      style={{
                        width: '100%', padding: '11px 0', background: (regName.trim() && regPhone.trim() && !registering) ? '#059669' : '#e2e8f0',
                        color: (regName.trim() && regPhone.trim() && !registering) ? '#fff' : '#9ca3af',
                        border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: (regName.trim() && regPhone.trim() && !registering) ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {registering ? 'Registering...' : 'Continue'}
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <BackBtn onClick={function() { setStep(0); }} />
              </div>
            </div>
          )}

          {/* ─── STEP 2: Therapist & Booking Type (auto-determined) ── */}
          {step === 2 && (
            <div>
              {loadingContext ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 14 }}>Loading patient details...</div>
              ) : bookingContext && !bookingContext.hasConsultation ? (
                /* ── FIRST-TIMER: Force Dr Deepak consultation ── */
                <div>
                  <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#1e40af', marginBottom: 4 }}>
                      {'\uD83C\uDD95'} First Consultation
                    </div>
                    <div style={{ fontSize: 12, color: '#3b82f6', lineHeight: 1.6 }}>
                      {tName}'s first visit requires a consultation with <strong>Dr. Deepak</strong> to assess the condition and create a treatment plan.
                    </div>
                  </div>
                  <div style={Object.assign({}, card(true, false), { display: 'flex', alignItems: 'center', gap: 14 })}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18, flexShrink: 0 }}>D</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>Dr. Deepak</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Consultation — Paid Service</div>
                    </div>
                    <span style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 20 }}>{'\u2713'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <BackBtn onClick={function() { setStep(bookingFor === 'self' ? 0 : 1); }} />
                    <NextBtn
                      label="Next: Date & Slot \u203A"
                      onClick={function() {
                        var drDeepak = { username: 'deepak', roles: ['doctor', 'owner'], designation: 'Doctor Deepak' };
                        setST(drDeepak);
                        setBookingType('consultation');
                        setIsFreeBooking(false);
                        setStep(3);
                      }}
                    />
                  </div>
                </div>
              ) : bookingContext && (bookingContext.pendingVisits > 0 || bookingContext.creditBalance > 0) ? (
                /* ── RETURNING with pending sessions / credit ── */
                <div>
                  <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#059669', marginBottom: 4 }}>
                      {'\u2705'} Sessions Available
                    </div>
                    <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.6 }}>
                      {bookingContext.pendingVisits > 0 && (
                        <span><strong>{bookingContext.pendingVisits}</strong> session(s) remaining. </span>
                      )}
                      {bookingContext.creditBalance > 0 && (
                        <span>Credit balance: <strong>{'\u20B9'}{bookingContext.creditBalance}</strong>. </span>
                      )}
                      <span>You can book a session below.</span>
                    </div>
                  </div>

                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 12 }}>
                    Choose Booking Type
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Review with Dr Deepak — FREE */}
                    <div
                      onClick={function() {
                        var drDeepak = { username: 'deepak', roles: ['doctor', 'owner'], designation: 'Doctor Deepak' };
                        setST(drDeepak);
                        setBookingType('review');
                        setIsFreeBooking(true);
                        setStep(3);
                      }}
                      style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 14 })}
                    >
                      <span style={{ fontSize: 28 }}>{'\uD83D\uDD04'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>Review with Dr. Deepak</div>
                        <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Follow-up review</div>
                      </div>
                      <div style={{ color: '#059669', fontSize: 18, fontWeight: 700 }}>{'\u203A'}</div>
                    </div>

                    {/* Therapy Session — FREE — pick therapist */}
                    <div
                      onClick={function() {
                        setBookingType('session');
                        setIsFreeBooking(true);
                        // Show therapist selection inline below
                        setST(null);
                      }}
                      style={Object.assign({}, card(bookingType === 'session' && isFreeBooking, false), { display: 'flex', alignItems: 'center', gap: 14 })}
                    >
                      <span style={{ fontSize: 28 }}>{'\uD83E\uDDD8'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>Therapy Session</div>
                        <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Yoga / Physio / Therapy</div>
                      </div>
                      {bookingType === 'session' && isFreeBooking && <span style={{ color: '#2563eb', fontSize: 20 }}>{'\u2713'}</span>}
                    </div>

                    {/* Therapist picker for free therapy session */}
                    {bookingType === 'session' && isFreeBooking && !selectedTherapist && (
                      <div style={{ paddingLeft: 16, borderLeft: '3px solid #2563eb' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Select Therapist</div>
                        {/* Dr Deepak */}
                        {(function() {
                          var drDeepak = { username: 'deepak', roles: ['doctor', 'owner'], designation: 'Doctor Deepak' };
                          return (
                            <div
                              onClick={function() { setST(drDeepak); setStep(3); }}
                              style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 })}
                            >
                              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>D</div>
                              <div><div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>Dr. Deepak</div></div>
                              <div style={{ marginLeft: 'auto', color: '#059669', fontSize: 16 }}>{'\u203A'}</div>
                            </div>
                          );
                        })()}
                        {/* Other therapists */}
                        {therapists.filter(function(t) { return !(t.username || '').toLowerCase().includes('deepak'); }).map(function(t) {
                          return (
                            <div key={t._id || t.username}
                              onClick={function() { setST(t); setStep(3); }}
                              style={Object.assign({}, card(false, false), { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 })}
                            >
                              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>
                                {(t.username || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{therapistDisplayName(t)}</div>
                                <div style={{ fontSize: 10, color: '#64748b' }}>{t.designation || ''}</div>
                              </div>
                              <div style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 16 }}>{'\u203A'}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <BackBtn onClick={function() { setStep(bookingFor === 'self' ? 0 : 1); setST(null); setIsFreeBooking(false); }} />
                  </div>
                </div>
              ) : (
                /* ── RETURNING, NO CREDIT — normal full flow ── */
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 14 }}>
                    Select your Doctor / Therapist
                  </div>

                  {/* Dr. Deepak — always shown first */}
                  {(function() {
                    var drDeepak = { username: 'deepak', roles: ['doctor', 'owner'], designation: 'Doctor Deepak' };
                    return (
                      <div
                        onClick={function() { setST(drDeepak); setBookingType('consultation'); setIsFreeBooking(false); setStep(2.5); }}
                        style={Object.assign({}, card(selectedTherapist && selectedTherapist.username === 'deepak', false), { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 })}>
                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18, flexShrink: 0 }}>D</div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>Dr. Deepak</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Doctor & Consultation Specialist</div>
                        </div>
                        <div style={{ marginLeft: 'auto', color: '#059669', fontSize: 18, fontWeight: 700 }}>{'\u203A'}</div>
                      </div>
                    );
                  })()}

                  {therapists.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Loading therapists...</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {therapists.filter(function(t) { return !(t.username || '').toLowerCase().includes('deepak'); }).map(function(t) {
                      return (
                        <div key={t._id || t.username}
                          onClick={function() { setST(t); setBookingType('consultation'); setIsFreeBooking(false); setStep(2.5); }}
                          style={Object.assign({}, card(selectedTherapist && selectedTherapist.username === t.username, false), { display: 'flex', alignItems: 'center', gap: 14 })}>
                          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                            {(t.username || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>{therapistDisplayName(t)}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{t.designation || (t.roles || []).filter(function(r) { return ['therapist','doctor'].indexOf(r) !== -1; }).join(', ')}</div>
                          </div>
                          <div style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 18 }}>{'\u203A'}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <BackBtn onClick={function() { setStep(bookingFor === 'self' ? 0 : 1); }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 2.5: Booking Type (for returning, no credit — same as old Step 1) ── */}
          {step === 2.5 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 16 }}>
                Select Appointment Type
                <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>with {therapistDisplayName(selectedTherapist)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { id: 'consultation', icon: '\uD83E\uDE7A', title: 'Consultation', sub: 'First visit or follow-up diagnosis' },
                  ...(isDeepakSelected ? [{ id: 'review', icon: '\uD83D\uDD04', title: 'Review', sub: 'Follow-up review (no charge)' }] : []),
                  { id: 'session', icon: '\uD83E\uDDD8', title: 'Therapy Session', sub: 'Scheduled yoga / physio / therapy session' },
                ].map(function(t) {
                  return (
                    <div key={t.id} onClick={function() { setBookingType(t.id); setIsFreeBooking(t.id === 'review'); }}
                      style={Object.assign({}, card(bookingType === t.id, false), { display: 'flex', alignItems: 'center', gap: 16 })}>
                      <span style={{ fontSize: 30 }}>{t.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>{t.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{t.sub}</div>
                      </div>
                      {bookingType === t.id && <span style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 20 }}>{'\u2713'}</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <BackBtn onClick={function() { setStep(2); setST(null); }} />
                <NextBtn label="Next: Date & Slot \u203A" onClick={function() { setStep(3); }} />
              </div>
            </div>
          )}

          {/* ─── STEP 3: Date + Slot ──────────────────────────── */}
          {step === 3 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 12 }}>
                Select Date &amp; Time Slot
                <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 8 }}>with {therapistDisplayName(selectedTherapist)}</span>
              </div>

              {isDeepakTherapist && (
                <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 11, color: '#1e40af', lineHeight: 1.6 }}>
                  Dr. Deepak is available <strong>Mon{'\u2013'}Sat</strong>, <strong>11 AM {'\u2013'} 2 PM</strong> &amp; <strong>4 PM {'\u2013'} 6 PM</strong>.
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Date</label>
                <input type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={selectedDate}
                  onChange={function(e) { setDate(e.target.value); setSlot(null); }}
                  style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              {isDeepakSunday ? (
                <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '20px 16px', textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{'\uD83D\uDEAB'}</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#991b1b', marginBottom: 4 }}>
                    Not Available on Sundays
                  </div>
                  <div style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.5 }}>
                    Dr. Deepak does not consult on Sundays.<br/>Please select another day (Mon{'\u2013'}Sat).
                  </div>
                </div>
              ) : therapistOnLeave ? (
                <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '20px 16px', textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{'\uD83C\uDFD6\uFE0F'}</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#991b1b', marginBottom: 4 }}>
                    Not Available
                  </div>
                  <div style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.5 }}>
                    {therapistDisplayName(selectedTherapist) || 'This therapist'} is on leave on {fmtDate(selectedDate)}.<br/>Please select another date or therapist.
                  </div>
                </div>
              ) : selectedDate && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                    Available Slots {'\u2014'} {fmtDate(selectedDate)}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isDeepakTherapist ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 8 }}>
                    {slots.map(function(sl, i) {
                      var busy = isSlotBusy(sl);
                      var past = isPastSlot(sl);
                      var sel  = selectedSlot && selectedSlot.label === sl.label;
                      return (
                        <button key={i} disabled={busy || past} onClick={function() { setSlot(sl); }}
                          style={{ padding: isDeepakTherapist ? '12px 8px' : '9px 6px', borderRadius: 10, fontSize: 12, fontWeight: 600, border: 'none',
                            cursor: busy || past ? 'not-allowed' : 'pointer',
                            background: sel ? '#2563eb' : busy || past ? '#f3f4f6' : '#f0fdf4',
                            color: sel ? '#fff' : busy || past ? '#9ca3af' : '#166534',
                            textDecoration: busy ? 'line-through' : 'none',
                          }}>
                          {sl.label}
                          {(busy || past) && <div style={{ fontSize: 9, fontWeight: 500 }}>{busy ? 'Not Available' : ''}</div>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <BackBtn onClick={function() {
                  if (bookingContext && !bookingContext.hasConsultation) { setStep(2); }
                  else if (bookingContext && (bookingContext.pendingVisits > 0 || bookingContext.creditBalance > 0)) { setStep(2); setST(null); }
                  else { setStep(2.5); }
                }} />
                <NextBtn label="Next: Mode \u203A" onClick={function() { setStep(4); }} disabled={!selectedSlot || isDeepakSunday || therapistOnLeave} />
              </div>
            </div>
          )}

          {/* ─── STEP 4: Mode ────────────────────────────────── */}
          {step === 4 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 16 }}>Select Session Mode</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { id: 'offline', icon: '\uD83C\uDFE5', title: 'Visit Centre', sub: 'Come to clinic for in-person session' },
                  { id: 'online',  icon: '\uD83C\uDF10', title: 'Online Session', sub: 'Join via Google Meet or Zoom from home' },
                ].map(function(m) {
                  return (
                    <div key={m.id} onClick={function() { setMode(m.id); }}
                      style={Object.assign({}, card(mode === m.id, false), { display: 'flex', alignItems: 'center', gap: 16 })}>
                      <span style={{ fontSize: 28 }}>{m.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>{m.title}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{m.sub}</div>
                      </div>
                      {mode === m.id && <span style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 20 }}>{'\u2713'}</span>}
                    </div>
                  );
                })}
              </div>
              {selectedSlot && (
                <div style={{ marginTop: 16, background: '#f0fdf4', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#166534' }}>
                  {'\u2713'} {fmtDate(selectedDate)} {'\u00B7'} {selectedSlot.label} {'\u00B7'} with {therapistDisplayName(selectedTherapist)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <BackBtn onClick={function() { setStep(3); }} />
                <NextBtn label={isFreeBooking ? 'Next: Confirm \u203A' : 'Next: Payment \u203A'} onClick={function() { setStep(5); }} />
              </div>
            </div>
          )}

          {/* ─── STEP 5: Payment (or Free Confirmation) ───────── */}
          {step === 5 && (
            <div>
              {!bookedAppt ? (
                isFreeBooking ? (
                  /* ── FREE BOOKING — skip payment ── */
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 12 }}>Confirm Booking</div>
                    <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#059669', marginBottom: 6 }}>{'\u2705'} Booking Summary</div>
                      <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.8 }}>
                        <div><strong>Patient:</strong> {tName} ({tCard})</div>
                        <div><strong>Type:</strong> {bookingType === 'review' ? 'Review' : 'Therapy Session'}</div>
                        <div><strong>With:</strong> {therapistDisplayName(selectedTherapist)}</div>
                        <div><strong>Date:</strong> {fmtDate(selectedDate)}</div>
                        <div><strong>Time:</strong> {selectedSlot && selectedSlot.label}</div>
                        <div><strong>Mode:</strong> {mode === 'online' ? 'Online' : 'At Centre'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <BackBtn onClick={function() { setStep(4); }} />
                      <button
                        disabled={booking}
                        onClick={handleBook}
                        style={{
                          flex: 2, padding: '11px 0',
                          background: booking ? '#e2e8f0' : '#059669',
                          color: booking ? '#9ca3af' : '#fff',
                          border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13,
                          cursor: booking ? 'not-allowed' : 'pointer',
                        }}>
                        {booking ? 'Booking\u2026' : '\u2713 Confirm Appointment'}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── PAID BOOKING — full payment flow ── */
                  <>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f', marginBottom: 12 }}>Payment</div>

                    {/* Fee breakdown */}
                    {selectedService ? (
                      <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 12 }}>
                        <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6 }}>Fee Breakdown</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#374151', marginBottom: 3 }}>
                          <span>{selectedService.name}</span><span>{'\u20B9'}{baseAmt}</span>
                        </div>
                        {sgstAmt > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: 3 }}>
                            <span>SGST @{selectedService.sgst}%</span><span>{'\u20B9'}{sgstAmt}</span>
                          </div>
                        )}
                        {cgstAmt > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', marginBottom: 3 }}>
                            <span>CGST @{selectedService.cgst}%</span><span>{'\u20B9'}{cgstAmt}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, borderTop: '1px dashed #86efac', marginTop: 6, paddingTop: 6, fontSize: 13 }}>
                          <span>Total</span><span style={{ color: '#059669' }}>{'\u20B9'}{totalAmt}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ background: '#fef3c7', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#92400e' }}>
                        {'\u26A0\uFE0F'} Service fee not configured. You will be informed of the fee at the centre.
                      </div>
                    )}

                    {/* Service selector if multiple match */}
                    {matchingServices.length > 1 && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                          Service / Package
                        </label>
                        <select
                          value={selectedService ? selectedService._id || '' : ''}
                          onChange={function(e) { setSelectedService(allServices.find(function(s) { return s._id === e.target.value; }) || null); }}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
                        >
                          {matchingServices.map(function(s) {
                            return <option key={s._id} value={s._id}>{s.name} {'\u2014'} {'\u20B9'}{s.amount + Math.round(s.amount * ((s.sgst || 0) + (s.cgst || 0)) / 100)}</option>;
                          })}
                        </select>
                      </div>
                    )}

                    {/* Payment method */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                      {[
                        { id: 'cash',   icon: '\uD83D\uDCB5', title: 'Cash at Centre', sub: 'Pay on arrival. Appointment confirmed instantly.' },
                        { id: 'online', icon: '\uD83D\uDCF1', title: 'Online UPI' + (totalAmt ? ' \u2014 \u20B9' + totalAmt : ''), sub: 'Scan QR & pay now. Confirmed within 1 hour.' },
                      ].map(function(p) {
                        return (
                          <div key={p.id} onClick={function() { setPayMode(p.id); }}
                            style={Object.assign({}, card(payMode === p.id, false), { display: 'flex', alignItems: 'center', gap: 16 })}>
                            <span style={{ fontSize: 28 }}>{p.icon}</span>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>{p.title}</div>
                              <div style={{ fontSize: 12, color: '#64748b' }}>{p.sub}</div>
                            </div>
                            {payMode === p.id && <span style={{ marginLeft: 'auto', color: '#2563eb', fontSize: 20 }}>{'\u2713'}</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* UPI Payment */}
                    {payMode === 'online' && (
                      <div style={{ marginBottom: 16 }}>
                        {upiVpa ? (
                          isMobileDevice() ? (
                            <div>
                              <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 10, textAlign: 'center' }}>
                                Pay {'\u20B9'}{totalAmt} via UPI
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                                {[
                                  { name: 'BHIM UPI', icon: '\uD83D\uDCF1', scheme: 'upi' },
                                  { name: 'Google Pay', icon: '\uD83D\uDCB3', scheme: 'tez' },
                                  { name: 'PhonePe', icon: '\uD83D\uDCF2', scheme: 'phonepe' },
                                  { name: 'Paytm', icon: '\uD83D\uDCB0', scheme: 'paytmmp' },
                                ].map(function(app) {
                                  var link = app.scheme + '://pay?pa=' + encodeURIComponent(upiVpa) + '&pn=' + encodeURIComponent(orgData ? orgData.name || 'MedicalYog' : 'MedicalYog') + '&am=' + totalAmt + '&tn=' + encodeURIComponent('Appt ' + qrRef) + '&cu=INR';
                                  return (
                                    <button key={app.scheme}
                                      onClick={function() { window.location.href = link; }}
                                      style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                        padding: '14px 10px', borderRadius: 12,
                                        border: '1.5px solid #e2e8f0', background: '#f8fafc',
                                        cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#1e3a5f',
                                      }}>
                                      <span style={{ fontSize: 20 }}>{app.icon}</span>
                                      {app.name}
                                    </button>
                                  );
                                })}
                              </div>
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                padding: '12px 10px', borderRadius: 12,
                                border: '1.5px dashed #d1d5db', background: '#fafafa',
                                fontSize: 12, color: '#64748b', marginBottom: 8,
                              }}>
                                {'\uD83D\uDCB3'} Card / Net Banking {'\u2014'} please pay at centre
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                                After paying via any app, click "Done {'\u2014'} I've Paid" below
                              </div>
                              <div style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 12px', marginTop: 10, fontSize: 11, color: '#92400e', lineHeight: 1.5, textAlign: 'center' }}>
                                {'\uD83D\uDCF8'} Please share the payment screenshot on WhatsApp to <strong>9891243444</strong> for quick confirmation.
                              </div>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                                Scan to pay {'\u20B9'}{totalAmt} {'\u2014'} refreshes in <strong>{qrTimer}s</strong>
                              </div>
                              <img
                                src={qrImageUrl(upiLink(upiVpa, orgData ? orgData.name || 'MedicalYog' : 'MedicalYog', totalAmt, qrRef))}
                                alt="UPI QR" width={200} height={200}
                                style={{ borderRadius: 12, border: '2px solid #e2e8f0' }}
                              />
                              <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontWeight: 600 }}>
                                UPI ID: {upiVpa}
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                After paying, click "Done {'\u2014'} I've Paid" below
                              </div>
                              <div style={{ background: '#fef3c7', borderRadius: 8, padding: '8px 12px', marginTop: 10, fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>
                                {'\uD83D\uDCF8'} Please share the payment screenshot on WhatsApp to <strong>9891243444</strong> for quick confirmation.
                              </div>
                            </div>
                          )
                        ) : (
                          <div style={{ background: '#fef3c7', borderRadius: 10, padding: '12px 14px', fontSize: 12, color: '#92400e' }}>
                            {'\u26A0\uFE0F'} UPI not configured by admin yet. Please pay cash at centre or call us.
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                      <BackBtn onClick={function() { setStep(4); }} />
                      <button
                        disabled={!payMode || booking}
                        onClick={handleBook}
                        style={{
                          flex: 2, padding: '11px 0',
                          background: payMode && !booking ? (payMode === 'cash' ? '#059669' : '#2563eb') : '#e2e8f0',
                          color: payMode && !booking ? '#fff' : '#9ca3af',
                          border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13,
                          cursor: payMode && !booking ? 'pointer' : 'not-allowed',
                        }}>
                        {booking ? 'Booking\u2026' :
                          payMode === 'cash' ? '\u2713 Confirm Appointment' :
                          payMode === 'online' ? '\u2713 Done \u2014 I\'ve Paid' :
                          'Select Payment Method'}
                      </button>
                    </div>
                  </>
                )
              ) : (
                /* ── BOOKING CONFIRMED — Full Summary ─── */
                <div style={{ padding: '4px 0' }}>
                  {/* Status banner */}
                  <div style={{
                    textAlign: 'center', padding: '18px 16px', borderRadius: 14, marginBottom: 16,
                    background: isFreeBooking
                      ? 'linear-gradient(135deg,#059669,#10b981)'
                      : bookedAppt.paymentStatus === 'awaiting_confirmation'
                        ? 'linear-gradient(135deg,#fbbf24,#f59e0b)' : 'linear-gradient(135deg,#059669,#10b981)',
                  }}>
                    <div style={{ fontSize: 36, marginBottom: 4 }}>
                      {bookedAppt.paymentStatus === 'awaiting_confirmation' ? '\u23F3' : '\u2705'}
                    </div>
                    <div style={{ fontWeight: 900, fontSize: 17, color: '#fff' }}>
                      {bookedAppt.paymentStatus === 'awaiting_confirmation' ? 'Payment Under Review' : 'Appointment Confirmed!'}
                    </div>
                    {isFreeBooking && (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                        Session booked successfully.
                      </div>
                    )}
                    {bookedAppt.paymentStatus === 'awaiting_confirmation' && (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                        We'll confirm within 1 hour after verifying payment.
                        <br/>Please share payment screenshot on WhatsApp to <strong>9891243444</strong>.
                      </div>
                    )}
                  </div>

                  {/* Detailed receipt */}
                  <div style={{ background: '#f8fafc', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ background: '#1e3a5f', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>Booking Summary</span>
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>TKT-{bookedAppt._id.slice(-6).toUpperCase()}</span>
                    </div>
                    <div style={{ padding: '14px 16px', fontSize: 13, color: '#374151', lineHeight: 2 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Patient</span>
                        <span style={{ fontWeight: 700 }}>{tName || tCard}</span>
                      </div>
                      {bookingFor === 'other' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>Booked by</span>
                          <span style={{ fontWeight: 600 }}>{patientName}{regRelation ? ' (' + regRelation + ')' : ''}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Therapist</span>
                        <span style={{ fontWeight: 700 }}>{therapistDisplayName(selectedTherapist)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Date</span>
                        <span style={{ fontWeight: 700 }}>{fmtDate(selectedDate)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Time</span>
                        <span style={{ fontWeight: 700 }}>{selectedSlot && selectedSlot.label}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Type</span>
                        <span style={{ fontWeight: 600 }}>{bookingType === 'consultation' ? 'Consultation' : bookingType === 'review' ? 'Review' : 'Therapy Session'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Mode</span>
                        <span style={{ fontWeight: 600 }}>{mode === 'online' ? '\uD83C\uDF10 Online Video' : '\uD83C\uDFE5 At Centre'}</span>
                      </div>

                      {/* Payment summary */}
                      {isFreeBooking ? null : totalAmt > 0 ? (
                        <>
                          <div style={{ borderTop: '1px dashed #d1d5db', margin: '8px 0' }} />
                          {selectedService && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b' }}>{selectedService.name}</span>
                              <span>{'\u20B9'}{baseAmt}</span>
                            </div>
                          )}
                          {sgstAmt > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span style={{ color: '#94a3b8' }}>SGST @{selectedService && selectedService.sgst}%</span>
                              <span style={{ color: '#94a3b8' }}>{'\u20B9'}{sgstAmt}</span>
                            </div>
                          )}
                          {cgstAmt > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                              <span style={{ color: '#94a3b8' }}>CGST @{selectedService && selectedService.cgst}%</span>
                              <span style={{ color: '#94a3b8' }}>{'\u20B9'}{cgstAmt}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14, borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 4 }}>
                            <span>Total</span>
                            <span style={{ color: '#059669' }}>{'\u20B9'}{totalAmt}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 2 }}>
                            <span style={{ color: '#64748b' }}>Payment</span>
                            <span style={{ fontWeight: 600 }}>{payMode === 'cash' ? 'Cash at Centre' : 'UPI Online'}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                            <span style={{ color: '#64748b' }}>Status</span>
                            <span style={{
                              fontWeight: 700,
                              color: bookedAppt.paymentStatus === 'awaiting_confirmation' ? '#d97706'
                                : bookedAppt.paymentStatus === 'confirmed' ? '#059669' : '#374151'
                            }}>
                              {bookedAppt.paymentStatus === 'awaiting_confirmation' ? 'Awaiting Confirmation'
                                : bookedAppt.paymentStatus === 'confirmed' ? 'Confirmed' : 'Confirmed'}
                            </span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={shareInvoice}
                      style={{ flex: 1, padding: '12px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                      {'\uD83D\uDCF2'} Share on WhatsApp
                    </button>
                    <button onClick={onClose}
                      style={{ flex: 1, padding: '12px 14px', background: 'linear-gradient(135deg,#2563eb,#059669)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                      Done {'\u2713'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
